// De logica achter het skillformulier en het skillmenu, zonder React, zodat
// vitest het zonder DOM kan draaien.
//
// Het formulier houdt zijn waarden in de vorm van de invoervelden (namen voor
// percelen, strings voor getallen); `toRequest` maakt er de waarden van die de
// server controleert.

import { SkillDefinition, SkillField } from "./types";

export type FormValue = string | string[] | { min: string; max: string };
export type FormValues = Record<string, FormValue>;

const emptyValue = (field: SkillField): FormValue => {
  if (field.type === "parcels" || field.type === "multi_choice") return [];
  if (field.type === "range") return { min: "", max: "" };
  return "";
};

// Een waarde van de server (een run of een default) in de vorm van het formulier.
export const fromServer = (field: SkillField, value: any): FormValue => {
  if (value === undefined || value === null) return emptyValue(field);
  if (field.type === "parcel") return typeof value === "object" ? value.name : String(value);
  if (field.type === "parcels" || field.type === "multi_choice") {
    return (value as any[]).map((item) => (typeof item === "object" ? item.name : String(item)));
  }
  if (field.type === "range") {
    return {
      min: value.min === undefined ? "" : String(value.min),
      max: value.max === undefined ? "" : String(value.max),
    };
  }
  return String(value);
};

/**
 * De beginwaarden: wat de run al had, anders de default, en een leeg
 * perceelveld krijgt het aangeklikte perceel -- maar alleen als dat een perceel
 * van het project is, want een ander kent de server niet.
 */
export function initialValues(
  fields: SkillField[],
  values: Record<string, any> = {},
  chipParcel?: string
): FormValues {
  const form: FormValues = {};
  for (const field of fields) {
    const given = field.name in values ? values[field.name] : field.default;
    form[field.name] = fromServer(field, given);
    if (field.type === "parcel" && !form[field.name] && chipParcel) {
      form[field.name] = chipParcel;
    }
  }
  return form;
}

/**
 * Een veld wijzigen. Een keuze kan andere velden meezetten (`sets`): beknopt
 * onderzoek zet de rapportblokken uit. Alleen op het moment van kiezen -- daarna
 * past de gebruiker die velden vrij aan.
 */
export function changeField(
  fields: SkillField[],
  form: FormValues,
  field: SkillField,
  value: FormValue
): FormValues {
  const next = { ...form, [field.name]: value };
  const effects = typeof value === "string" ? field.sets?.[value] : undefined;
  for (const [name, set] of Object.entries(effects ?? {})) {
    const target = fields.find((item) => item.name === name);
    if (target) next[name] = fromServer(target, set);
  }
  return next;
}

const isEmpty = (value: FormValue | undefined) => {
  if (value === undefined) return true;
  if (typeof value === "string") return value.trim() === "";
  if (Array.isArray(value)) return value.length === 0;
  return value.min.trim() === "" && value.max.trim() === "";
};

/** De labels van verplichte velden die nog leeg zijn. */
export const missingRequired = (fields: SkillField[], form: FormValues) =>
  fields.filter((field) => field.required && isEmpty(form[field.name])).map((f) => f.label);

// Tot zoveel velden toont het formulier alles; daarboven alleen wat ertoe doet.
const MANY_FIELDS = 8;

/**
 * Welke velden een formulier bij het openen toont. Een skill met veel velden
 * (de zoekskill heeft er twintig, een aangeboden filter gebruikt er twee) toont
 * de verplichte en de ingevulde; de rest staat achter "Meer velden". Blijft er
 * dan niets over (de zoekskill uit het menu), dan alles: een leeg formulier
 * zegt niet wat je kunt invullen.
 *
 * Bedoeld voor de beginwaarden, één keer: een veld dat de gebruiker leegmaakt
 * hoort niet onder zijn handen te verdwijnen.
 */
export function visibleFields(fields: SkillField[], form: FormValues): SkillField[] {
  if (fields.length <= MANY_FIELDS) return fields;
  const shown = fields.filter((field) => field.required || !isEmpty(form[field.name]));
  return shown.length === 0 ? fields : shown;
}

/**
 * De waarden voor de server. Lege tekst en lege bereiken gaan niet mee; een
 * lege lijst wel: een bewust leeggemaakte keuze betekent "geen", en weglaten
 * zou de server de default laten invullen.
 */
export function toRequest(fields: SkillField[], form: FormValues): Record<string, any> {
  const values: Record<string, any> = {};
  for (const field of fields) {
    const value = form[field.name];
    if (value === undefined) continue;
    if (Array.isArray(value)) {
      values[field.name] = value;
    } else if (typeof value === "string") {
      if (value.trim()) values[field.name] = value.trim();
    } else {
      const range: Record<string, number> = {};
      if (value.min.trim()) range.min = Number(value.min);
      if (value.max.trim()) range.max = Number(value.max);
      if (Object.keys(range).length) values[field.name] = range;
    }
  }
  return values;
}

/**
 * Eén veld in de vorm van de server. Leeg blijft leeg in plaats van weg te
 * vallen: een vastgezet leeg veld betekent "geen" (geen eigen vraag).
 */
export function toServer(field: SkillField, value: FormValue): any {
  const converted = toRequest([field], { [field.name]: value })[field.name];
  if (converted !== undefined) return converted;
  return field.type === "range" ? {} : "";
}

/**
 * Wat er na "/" in het invoerveld staat, zolang het één regel is. Spaties horen
 * erbij: skills heten "Kavels zoeken".
 */
export function slashQuery(input: string): string | undefined {
  const match = /^\/([^\n]*)$/.exec(input);
  return match ? match[1] : undefined;
}

/** De gemarkeerde regel een stap verder, rond aan beide einden. */
export const moveHighlight = (index: number, step: number, count: number) =>
  count === 0 ? 0 : (index + step + count) % count;

/** De skills die bij een zoekterm passen, op label of naam. */
export function matchingSkills(skills: SkillDefinition[], query: string): SkillDefinition[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return skills;
  return skills.filter(
    (skill) =>
      skill.label.toLowerCase().includes(needle) || skill.name.toLowerCase().includes(needle)
  );
}
